import {Action} from 'redux';

import ModalTypes from '../../enums/app/modalTypes';
import DEVICE_TYPES from '../../enums/device/deviceTypes';
import FWU_TARGET_TYPES from '../../enums/iot/fwuTargetTypes';
import {IModalActionProps, IModalState} from '../types/modal';

enum ActionTypes {
    SHOW_MODAL = 'app/modal/SHOW_MODAL',
    HIDE_MODAL = 'app/modal/HIDE_MODAL',
    HIDE_ALL_MODALS = 'app/modal/HIDE_ALL_MODALS',
    ADD_MODAL_TO_RENDERED_MODALS = 'app/modal/ADD_MODAL_TO_RENDERED_MODALS',
    REMOVE_MODAL_FROM_RENDERED_MODALS = 'app/modal/REMOVE_MODAL_FROM_RENDERED_MODALS',
}
interface IModalAction extends Action {
    type: ActionTypes;
    payload: IModalActionProps | string;
}

const initialState: IModalState = {
    globalModals: [],
    renderedModals: [],
};

const DEFAULT_MODAL_ORDER = 1000;
const HIGHEST_MODAL_ORDER = 99999;

export default (state = initialState, action: IModalAction): IModalState => {
    switch (action.type) {
        case ActionTypes.SHOW_MODAL: {
            const {
                modalType,
                modalProps,
                order = DEFAULT_MODAL_ORDER,
                alwaysDisplay = false,
            } = action.payload as IModalActionProps;

            if (state.globalModals.some((modal) => modal.modalType === modalType)) {
                return state;
            } else {
                return {
                    ...state,
                    globalModals: [
                        ...state.globalModals,
                        {
                            modalType,
                            modalProps,
                            order,
                            alwaysDisplay,
                        },
                    ],
                };
            }
        }
        case ActionTypes.HIDE_MODAL: {
            const {modalType} = action.payload as IModalActionProps;

            return {...state, globalModals: state.globalModals.filter((modal) => modal.modalType !== modalType)};
        }
        case ActionTypes.ADD_MODAL_TO_RENDERED_MODALS: {
            const id = action.payload as string;

            if (state.renderedModals.some((modalId) => modalId === id)) {
                return state;
            } else {
                return {
                    ...state,
                    renderedModals: [...state.renderedModals, id],
                };
            }
        }
        case ActionTypes.REMOVE_MODAL_FROM_RENDERED_MODALS: {
            const id = action.payload as string;

            return {...state, renderedModals: state.renderedModals.filter((modalId) => modalId !== id)};
        }
        case ActionTypes.HIDE_ALL_MODALS: {
            return {...state, globalModals: [], renderedModals: []};
        }
        default:
            return state;
    }
};

export const showHidReconnectModalAction = (modalProps?: unknown): IModalAction =>
    showModalAction({modalType: ModalTypes.HID_RECONNECT, modalProps});

interface IDeviceIsInSblModalProps {
    isInitialFlow?: boolean;
    fwu_target: FWU_TARGET_TYPES;
    deviceType: DEVICE_TYPES;
}

export const showDeviceIsInSblModalAction = (modalProps: IDeviceIsInSblModalProps): IModalAction =>
    showModalAction({modalType: ModalTypes.DEVICE_IS_IN_SBL, modalProps, order: 0});

export const showReconnectDeviceGuideModalAction = (modalProps?: unknown): IModalAction =>
    showModalAction({modalType: ModalTypes.RECONNECT_DEVICE_GUIDE, modalProps});

export const showFWUAdditionalStepsGuideAction = (modalProps?: unknown): IModalAction =>
    showModalAction({modalType: ModalTypes.FWU_ADDITIONAL_STEPS_GUIDE, modalProps});

export const showPurchaseDateModalAction = (modalProps?: unknown): IModalAction =>
    showModalAction({modalType: ModalTypes.PURCHASE_DATE, modalProps});

export const showDeviceHolderRegistrationModalAction = (modalProps?: unknown): IModalAction => {
    return showModalAction({modalType: ModalTypes.REGISTRATION_HOLDER, modalProps});
};

export const showFirmwareReleaseNotesModalAction = (modalProps?: unknown): IModalAction =>
    showModalAction({modalType: ModalTypes.FIRMWARE_RELEASE_NOTES_POPUP, modalProps, order: HIGHEST_MODAL_ORDER});

export const showFWULedCheckAction = (modalProps?: unknown): IModalAction =>
    showModalAction({modalType: ModalTypes.FWU_LED_CHECK, modalProps});

export const showFWUErrorModalAction = (modalProps?: unknown): IModalAction =>
    showModalAction({modalType: ModalTypes.FWU_ERROR, modalProps});

export const showDeviceLockedModalAction = (): IModalAction => showModalAction({modalType: ModalTypes.DEVICE_LOCKED});

export const showManualRegistrationModalAction = (modalProps?: unknown): IModalAction =>
    showModalAction({modalType: ModalTypes.MANUAL_REGISTRATION, modalProps});

export const showUWMUnavailabilityModalAction = (modalProps?: unknown): IModalAction =>
    showModalAction({modalType: ModalTypes.UWM_UNAVAILABILITY, modalProps});

export const showOutsideRegistrationModalAction = (modalProps?: unknown): IModalAction =>
    showModalAction({modalType: ModalTypes.OUTSIDE_REGISTRATION, modalProps});

export const showOutsideClickModalAction = (modalProps?: unknown): IModalAction =>
    showModalAction({modalType: ModalTypes.OUTSIDE_CLICK_POPUP, modalProps});

export const showLoyaltyClubModalAction = (modalProps?: unknown): IModalAction =>
    showModalAction({modalType: ModalTypes.LOYALTY_CLUB, modalProps});

export const showPauseModeModalAction = (modalProps?: unknown): IModalAction =>
    showModalAction({modalType: ModalTypes.PAUSE_MODE, modalProps});

export const showLeviaLearnMoreModalAction = (modalProps?: unknown): IModalAction =>
    showModalAction({modalType: ModalTypes.LEVIA_LEAR_MORE, modalProps, order: HIGHEST_MODAL_ORDER});

export const showLeviaFwuModalAction = (modalProps?: unknown): IModalAction =>
    showModalAction({modalType: ModalTypes.LEVIA_FWU, modalProps, order: HIGHEST_MODAL_ORDER});

export const showQuickStartGuideModalAction = (modalProps?: unknown): IModalAction =>
    showModalAction({
        modalType: ModalTypes.QUICK_START_GUIDE_POPUP,
        modalProps,
        order: HIGHEST_MODAL_ORDER - 1,
        alwaysDisplay: true,
    });

export const showConsumerFeedbackGatheringModalAction = (modalProps?: unknown): IModalAction =>
    showModalAction({modalType: ModalTypes.CONSUMER_FEEDBACK_GATHERING, modalProps});

export const showModalAction = ({modalType, modalProps, order, alwaysDisplay}: IModalActionProps): IModalAction => ({
    type: ActionTypes.SHOW_MODAL,
    payload: {modalType, modalProps, order, alwaysDisplay},
});

export const hideModalAction = (modalType: ModalTypes): IModalAction => ({
    type: ActionTypes.HIDE_MODAL,
    payload: {modalType},
});

export const hideAllModalsAction = () => ({
    type: ActionTypes.HIDE_ALL_MODALS,
});

export const addModalToRenderedModals = (id: string): IModalAction => ({
    type: ActionTypes.ADD_MODAL_TO_RENDERED_MODALS,
    payload: id,
});

export const removeModalFromRenderedModals = (id: string): IModalAction => ({
    type: ActionTypes.REMOVE_MODAL_FROM_RENDERED_MODALS,
    payload: id,
});

export const showUsbRequiredModalAction = (modalProps?: unknown): IModalAction =>
    showModalAction({modalType: ModalTypes.USB_REQUIRED, modalProps});
